import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import productService from '../../../services/productService';

/**
 * Records a manual stock movement:
 *  - ADJUSTMENT: set the variant's stock to a counted quantity (POST /stock-movements/adjust)
 *  - DAMAGE:     remove damaged/lost units (POST /stock-movements, negative quantityChange)
 *  - TRANSFER:   move units to another store (POST /stock-movements/transfer); the product/variant is
 *                created in the destination store if it doesn't exist there yet
 *
 * Pass `variant` ({ id, name, sku, stockQuantity, reservedQuantity }) plus `storeId` (its store) to lock the
 * variant (opened from a product);
 * otherwise the user picks a product, then one of its variants.
 */
const StockAdjustmentDialog = ({ open, onClose, onSaved, variant: fixedVariant = null, storeId = null }) => {
  const { t } = useTranslation();

  const [type, setType] = useState('ADJUSTMENT');
  const [product, setProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [productInput, setProductInput] = useState('');
  const [variants, setVariants] = useState([]);
  const [variantId, setVariantId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [destinations, setDestinations] = useState([]);
  const [destinationId, setDestinationId] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setType('ADJUSTMENT');
    setProduct(null);
    setVariants([]);
    setVariantId(fixedVariant?.id || '');
    setQuantity('');
    setReason('');
    setDestinationId('');
  }, [open, fixedVariant]);

  // Product search is server-side (paged endpoint), debounced.
  useEffect(() => {
    if (!open || fixedVariant) return undefined;
    const timer = setTimeout(() => {
      productService
        .getProductsPage({ search: productInput, size: 10 })
        .then((page) => setProductOptions(page.content || []))
        .catch(() => setProductOptions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [open, fixedVariant, productInput]);

  useEffect(() => {
    if (!product) {
      setVariants([]);
      setVariantId('');
      return;
    }
    productService
      .getProductVariants(product.id)
      .then((list) => setVariants(list || []))
      .catch(() => setVariants([]));
  }, [product]);

  // Store the stock currently lives in (to leave it out of the destinations).
  const sourceStoreId = fixedVariant ? storeId : product?.storeId || null;

  useEffect(() => {
    if (!open || type !== 'TRANSFER') return;
    productService
      .getTransferDestinations(sourceStoreId)
      .then((list) => setDestinations(list || []))
      .catch(() => setDestinations([]));
  }, [open, type, sourceStoreId]);

  const selected = fixedVariant || variants.find((v) => v.id === variantId) || null;
  const currentStock = selected?.stockQuantity ?? null;
  const reservedStock = selected?.reservedQuantity ?? 0;
  // Transfers can only move what isn't reserved by pending orders.
  const maxQty = type === 'TRANSFER' ? (currentStock ?? 0) - reservedStock : currentStock;

  const qty = quantity === '' ? NaN : Number(quantity);
  const qtyValid =
    Number.isInteger(qty) && (type === 'ADJUSTMENT' ? qty >= 0 : qty > 0 && (maxQty === null || qty <= maxQty));
  const canSave =
    Boolean(selected) && qtyValid && reason.trim().length > 0 && !saving && (type !== 'TRANSFER' || Boolean(destinationId));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (type === 'ADJUSTMENT') {
        await productService.adjustStock({
          variantId: selected.id,
          newStockQuantity: qty,
          reason: reason.trim(),
        });
      } else if (type === 'TRANSFER') {
        const result = await productService.transferStock({
          variantId: selected.id,
          destinationStoreId: destinationId,
          quantity: qty,
          reason: reason.trim(),
        });
        toast.info(
          result.productCreated || result.variantCreated
            ? t('stockMovements.transferCreated', { store: result.destinationStoreName })
            : t('stockMovements.transferDone', { store: result.destinationStoreName })
        );
      } else {
        await productService.createStockMovement({
          variantId: selected.id,
          quantityChange: -qty,
          movementType: 'DAMAGE',
          referenceType: 'MANUAL_DAMAGE',
          notes: reason.trim(),
        });
      }
      toast.success(t('stockMovements.saveSuccess'));
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>{t('stockMovements.newMovement')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={type}
          onChange={(_, next) => next && (setType(next), setQuantity(''))}
          sx={{ mt: 1 }}
        >
          <ToggleButton value="ADJUSTMENT">{t('stockMovements.types.ADJUSTMENT')}</ToggleButton>
          <ToggleButton value="DAMAGE">{t('stockMovements.damageLoss')}</ToggleButton>
          <ToggleButton value="TRANSFER">{t('stockMovements.types.TRANSFER')}</ToggleButton>
        </ToggleButtonGroup>

        {fixedVariant ? (
          <Typography variant="body2">
            <strong>{fixedVariant.shortName || fixedVariant.name}</strong> — {fixedVariant.sku}
          </Typography>
        ) : (
          <>
            <Autocomplete
              options={productOptions}
              value={product}
              onChange={(_, next) => setProduct(next)}
              inputValue={productInput}
              onInputChange={(_, next) => setProductInput(next)}
              getOptionLabel={(p) => `${p.name} (${p.sku})`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              filterOptions={(x) => x}
              noOptionsText={t('common.noData')}
              renderInput={(params) => <TextField {...params} label={t('stockMovements.product')} />}
            />
            <FormControl fullWidth disabled={!product}>
              <InputLabel>{t('stockMovements.variant')}</InputLabel>
              <Select value={variantId} label={t('stockMovements.variant')} onChange={(e) => setVariantId(e.target.value)}>
                {variants.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {(v.shortName || v.name)} — {v.sku}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {currentStock !== null && (
          <Alert severity="info" icon={false}>
            {t('stockMovements.currentStock', { count: currentStock })}
          </Alert>
        )}

        {type === 'TRANSFER' && (
          <>
            <FormControl fullWidth>
              <InputLabel>{t('stockMovements.destinationStore')}</InputLabel>
              <Select
                value={destinationId}
                label={t('stockMovements.destinationStore')}
                onChange={(e) => setDestinationId(e.target.value)}
              >
                {destinations.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name} ({d.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {destinationId && (
              <Alert severity="info">{t('stockMovements.transferAutoCreateHint')}</Alert>
            )}
          </>
        )}

        <TextField
          fullWidth
          type="number"
          disabled={!selected || (type !== 'ADJUSTMENT' && (maxQty ?? 0) <= 0)}
          label={
            type === 'ADJUSTMENT'
              ? t('stockMovements.newStock')
              : type === 'TRANSFER'
                ? t('stockMovements.transferQuantity')
                : t('stockMovements.damagedQuantity')
          }
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          error={Boolean(selected) && ((type !== 'ADJUSTMENT' && (maxQty ?? 0) <= 0) || (quantity !== '' && !qtyValid))}
          helperText={
            !selected
              ? t('stockMovements.selectVariantFirst')
              : type !== 'ADJUSTMENT' && (maxQty ?? 0) <= 0
                ? t('stockMovements.noAvailable', { stock: currentStock ?? 0, reserved: reservedStock })
                : quantity !== '' && !qtyValid
                  ? type === 'ADJUSTMENT'
                    ? t('stockMovements.invalidNewStock')
                    : type === 'TRANSFER'
                      ? t('stockMovements.invalidTransfer', { max: maxQty })
                      : t('stockMovements.invalidDamaged')
                  : type === 'TRANSFER' && qtyValid
                    ? t('stockMovements.transferRest', { count: currentStock - qty, reserved: reservedStock })
                    : type === 'DAMAGE' && qtyValid && currentStock !== null
                      ? t('stockMovements.stockAfter', { count: currentStock - qty })
                      : type === 'ADJUSTMENT' && qtyValid && currentStock !== null
                        ? t('stockMovements.variation', { count: qty - currentStock })
                        : ' '
          }
          inputProps={{ min: 0, step: 1 }}
          InputProps={
            type !== 'ADJUSTMENT' && (maxQty ?? 0) > 0
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button size="small" onClick={() => setQuantity(String(maxQty))}>
                        {t('stockMovements.max')}
                      </Button>
                    </InputAdornment>
                  ),
                }
              : undefined
          }
        />

        <TextField
          fullWidth
          multiline
          minRows={2}
          label={t('stockMovements.reason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
        />
        <Box />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockAdjustmentDialog;
